/**
 * DEV-only store for F5-twice / stuck-loading debug.
 * - Chunk/asset errors (from window error + unhandledrejection)
 * - Loading gate snapshot (pathname, flags) when loading > 1500ms
 * - Content-type warnings (JS requested but got HTML)
 */

export interface ChunkErrorState {
  count: number;
  lastMessage: string;
}

export interface GateSnapshot {
  pathname: string;
  search: string;
  timestamp: number;
  /** DEV: number of gate renders while loading */
  renderCount?: number;
  /** true when snapshot taken at 6s (still loading) */
  secondSnapshot?: boolean;
  flags: {
    permissionsLoading: boolean;
    authMeLoading: boolean;
    permissionsSuccess: boolean;
    hasCurrentUser: boolean;
    showInitialLoader: boolean;
    isLoading: boolean;
    hasAccess: boolean | null;
  };
}

export interface BootDebugState {
  chunkErrors: ChunkErrorState;
  gateSnapshot: GateSnapshot | null;
  overlayVisible: boolean;
  contentTypeWarnings: string[];
  columnHydrating: { key: string; isHydrating: boolean } | null;
}

const initialState: BootDebugState = {
  chunkErrors: { count: 0, lastMessage: '' },
  gateSnapshot: null,
  overlayVisible: false,
  contentTypeWarnings: [],
  columnHydrating: null,
};

let state: BootDebugState = { ...initialState };
const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => fn());
}

export function getBootDebugState(): BootDebugState {
  return {
    ...state,
    chunkErrors: { ...state.chunkErrors },
    gateSnapshot: state.gateSnapshot ? { ...state.gateSnapshot, flags: { ...state.gateSnapshot.flags } } : null,
    contentTypeWarnings: [...state.contentTypeWarnings],
    columnHydrating: state.columnHydrating ? { ...state.columnHydrating } : null,
  };
}

export function subscribeBootDebug(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function reportChunkError(message: string): void {
  state.chunkErrors = {
    count: state.chunkErrors.count + 1,
    lastMessage: message,
  };
  notify();
}

export function setGateSnapshot(snapshot: GateSnapshot | null): void {
  state.gateSnapshot = snapshot;
  notify();
}

export function setOverlayVisible(visible: boolean): void {
  state.overlayVisible = visible;
  notify();
}

export function addContentTypeWarning(message: string): void {
  state.contentTypeWarnings = [...state.contentTypeWarnings, message];
  notify();
}

export function setColumnHydrating(value: { key: string; isHydrating: boolean } | null): void {
  state.columnHydrating = value;
  notify();
}

export function resetBootDebug(): void {
  state = {
    ...initialState,
    chunkErrors: { count: 0, lastMessage: '' },
    gateSnapshot: null,
    overlayVisible: false,
    contentTypeWarnings: [],
    columnHydrating: state.columnHydrating,
  };
  notify();
}
